import React from 'react'
import AdminPageWithSmartview from 'pages/app/AdminPageWithSmartview'
import { CardGroup, PageHeader, WithSubtitle } from 'ui/layouts'
import { Translation, Translator } from '@morningtrain/react-translations'
import {
  Collection,
  ForEachModel,
  Model,
  Selection,
  TriggerPipelineOnOperationExecute,
  ValueToCollection,
  WhenCollectionIsNotEmpty,
  WhenModelValue
} from '@morningtrain/react-resources'
import ResourceFilter from 'support/filters/ResourceFilter'
import { ModelValueFilter } from '@morningtrain/react-filters'
import { SelectedDepartmentFilter } from 'support/filters'
import { SecondaryHeading } from 'ui/headings'
import * as Displays from 'support/displays'
import * as Columns from 'support/columns'
import { Table } from 'widgets/crud'
import { Card, CardHeader } from 'ui/cards'
import MountSmartviewOnClickTableRow from 'widgets/crud/MountSmartviewOnClickTableRow'
import UserSalaryOverviewSmartview from 'widgets/smartviews/hr/salary/UserSalaryOverviewSmartview'
import { Pipeline } from '@morningtrain/react-helpers'
import { MountSmartviewWithModelOnPipe } from '@morningtrain/react-smartviews'
import SalaryOverviewSmartview from 'widgets/smartviews/hr/salary/SalaryOverviewSmartview'
import Subtitle from 'pages/app/hr/salary/EmployeeSubtitle'
import SalaryOverviewTitle from 'widgets/salary/SalaryOverviewTitle'
import AddModelValueToEnv from 'support/helpers/AddModelValueToEnv'
import * as Auth from '@morningtrain/react-auth'
import UserChangesButton from 'pages/app/hr/salary/partials/UserChangesButton'

export default function SalaryOverview (props) {
  return (
    <AdminPageWithSmartview>
      <Translator namespace="hr.salary.overview">
        <Model resourceName="salary.salary_period" operationName="find">

          <Auth.Can permission="hr.salary_manage">
            <Pipeline>
              <TriggerPipelineOnOperationExecute/>
              <MountSmartviewWithModelOnPipe smartview={<SalaryOverviewSmartview/>} replace/>
            </Pipeline>
          </Auth.Can>

          <PageHeader title={<SalaryOverviewTitle name="title"/>}>
            <ResourceFilter
              name="id"
              className="w-44"
              resourceName="salary.salary_period"
              optionsKey="display_name"
              label={false}
            />
          </PageHeader>

          <WhenModelValue name='id'>

            <AddModelValueToEnv name='period_starts_at' path='salary.current_period.period_starts_at' />
            <AddModelValueToEnv name='period_ends_at' path='salary.current_period.period_ends_at' />

            <Collection resourceName='salary.salary_period' operationName='overview'>
              <ModelValueFilter name='salary_period' modelKey='id' />
              <SelectedDepartmentFilter />

              <WhenCollectionIsNotEmpty>

                <CardGroup>
                  <ForEachModel>

                    <Card fullWidth>
                      <CardHeader withPadding>
                        <SecondaryHeading>
                          <Displays.Text name='key' />
                        </SecondaryHeading>
                      </CardHeader>
                      <ValueToCollection name='collection'>
                        <ForEachModel>
                          <ValueToCollection name='collection'>
                            <Selection>
                              <WhenModelValue name="key" condition="hourly">
                                <Table row={<MountSmartviewOnClickTableRow activeGroup="salary_overview_table_row"
                                                                           smartview={
                                                                             <UserSalaryOverviewSmartview/>}/>}>
                                  <Columns.Text
                                    name="employee_salary_id"
                                    decimals={0}
                                    className="w-14"
                                    label={<Translation name="columns.employee_salary_id"/>}
                                  />
                                  <Columns.Column label={<Translation name="columns.name"/>}>
                                    <WithSubtitle subtitle={<Subtitle/>}>
                                      <Displays.Text name="name"/>
                                    </WithSubtitle>
                                  </Columns.Column>
                                  <Columns.Number
                                    name="shifts_count"
                                    decimals={0}
                                    className="w-48"
                                    alignment="right"
                                    label={<Translation name="columns.shifts_count"/>}
                                  />
                                  <Columns.Duration
                                    name="hours_worked"
                                    alignment="right"
                                    className='w-48'
                                    label={<Translation name='columns.hours_worked' />}
                                  />
                                  <Columns.Currency
                                    name="wage"
                                    decimals={0}
                                    alignment="right"
                                    className="w-48"
                                    label={<Translation name="columns.hourly_wage"/>}
                                  />
                                  <Columns.Currency
                                    name="amount"
                                    className="w-48"
                                    alignment="right"
                                    label={<Translation name="columns.salary_amount"/>}
                                  />
                                  <Columns.Column alignment="right">
                                    <WhenModelValue
                                      name="has_unread_user_changes"
                                      condition={(hasUnreadChanges) => !!hasUnreadChanges}
                                    >
                                      <UserChangesButton/>
                                    </WhenModelValue>
                                  </Columns.Column>
                                </Table>
                              </WhenModelValue>
                              <WhenModelValue name='key' condition='monthly'>
                                <Table row={<MountSmartviewOnClickTableRow smartview={<UserSalaryOverviewSmartview />} />}>
                                  <Columns.Text
                                    name='employee_salary_id'
                                    decimals={0}
                                    className='w-14'
                                    label={<Translation name='columns.employee_salary_id' />}
                                  />
                                  <Columns.Column label={<Translation name='columns.name' />}>
                                    <WithSubtitle subtitle={<Subtitle />}>
                                      <Displays.Text name='name' />
                                    </WithSubtitle>
                                  </Columns.Column>
                                  <Columns.Number
                                    name="shifts_count"
                                    decimals={0}
                                    alignment='right'
                                    className='w-14'
                                    label={<Translation name='columns.shifts_count' />}
                                  />
                                  <Columns.Duration
                                    name="hours_worked"
                                    alignment="right"
                                    className='w-36'
                                    label={<Translation name='columns.hours_worked' />}
                                  />
                                  <Columns.Duration
                                    name='hours_worked'
                                    alignment='right'
                                    className='w-36'
                                    label={<Translation name='columns.hours_worked_total' />}
                                  />
                                  <Columns.Column
                                    alignment='right'
                                    className='w-40'
                                    label={<Translation name='columns.planned_hours' />}
                                  >
                                    <Displays.Duration name='planned_shifts' />
                                    <span> / </span>
                                    <Displays.Duration name='expected_hours' />
                                  </Columns.Column>
                                  <Columns.Duration
                                    name='remaining_hours'
                                    alignment='right'
                                    className='w-36'
                                    label={<Translation name='columns.remaining_hours' />}
                                  />
                                  <Columns.Currency
                                    name='wage'
                                    className='w-48'
                                    alignment='right'
                                    label={<Translation name='columns.salary_amount' />}
                                  />
                                </Table>
                              </WhenModelValue>
                            </Selection>
                          </ValueToCollection>
                        </ForEachModel>
                      </ValueToCollection>
                    </Card>

                  </ForEachModel>
                </CardGroup>

              </WhenCollectionIsNotEmpty>

            </Collection>

          </WhenModelValue>

        </Model>
      </Translator>
    </AdminPageWithSmartview>
  )
}
